import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Paper,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  ButtonGroup,
  Switch,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";

import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";

import api from "../../../services/api";

// ---------------------------------------------------------
// Logo config
// ---------------------------------------------------------
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const DEFAULT_LOGO_PATH = "/static/assets/defaultlogo.png";

// ---------------------------------------------------------
// Palette
// ---------------------------------------------------------
const palette = {
  indigo: "rgb(75, 73, 171)",
  indigoDark: "#37357D",
  indigoSoft: "#EEEDFB",
  ink: "#1F2937",
  slate: "#6B7280",
  line: "#E6E9ED",
  bg: "#FAFBFC",
  teal: "#059669",
  tealSoft: "#ECFDF5",
  coral: "#FF6B57",
  coralSoft: "#FFEFEC",
  amber: "#D97706",
  amberSoft: "#FFFBEB",
};

// ---------------------------------------------------------
// Constants
// ---------------------------------------------------------
const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CURRENT_YEAR = new Date().getFullYear();

const YEAR_OPTIONS = Array.from(
  { length: 6 },
  (_, i) => CURRENT_YEAR - 4 + i
);

// ---------------------------------------------------------
// Helpers
// ---------------------------------------------------------
const currency = (value) => {
  const num = Number(value ?? 0);

  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";

  const date = new Date(dateStr);

  if (isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatTime = (timeStr) => {
  if (!timeStr) return "";

  const [h, m] = timeStr.split(":");
  const hour = Number(h);

  const suffix = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return `${hour12}:${m} ${suffix}`;
};

// ---------------------------------------------------------
// Adjustment description helper
// ---------------------------------------------------------
// The API doesn't return a `description` field for adjustments,
// so we build a readable one from the data that is available.
// Preference order:
//   1. service_name + original_session_date (most precise)
//   2. service_name + source_month/source_year (fallback if no session date)
//   3. generic "Adjustment" (last resort)
const getAdjustmentDescription = (a) => {
  const sessionDate = a.original_session_date
    ? formatDate(a.original_session_date)
    : null;

  const sourcePeriod =
    a.source_month && a.source_year
      ? `${MONTH_NAMES[a.source_month - 1]} ${a.source_year}`
      : null;

  if (a.service_name && sessionDate) {
    return `Carry forward — ${a.service_name} session on ${sessionDate}`;
  }

  if (a.service_name && sourcePeriod) {
    return `Carry forward — ${a.service_name} session (${sourcePeriod})`;
  }

  return "Adjustment";
};

// ---------------------------------------------------------
// Status styles
// ---------------------------------------------------------
const STATUS_STYLES = {
  PRESENT: {
    color: "#0B6B5F",
    bg: palette.tealSoft,
  },

  ABSENT: {
    color: palette.coral,
    bg: palette.coralSoft,
  },

  SCHEDULED: {
    color: palette.slate,
    bg: "#F1F5F9",
  },

  CANCELLED: {
    color: palette.amber,
    bg: palette.amberSoft,
  },
};

const BILLING_STATUS_STYLES = {
  BILLED: {
    color: "#0B6B5F",
    bg: palette.tealSoft,
  },

  NOT_CONSIDERED: {
    color: palette.slate,
    bg: "#F1F5F9",
  },
};

// ---------------------------------------------------------
// Components
// ---------------------------------------------------------
const StatusChip = ({ value, styles }) => {
  const s =
    styles[value] || {
      color: palette.slate,
      bg: "#F1F5F9",
    };

  return (
    <Chip
      size="small"
      label={value?.replace(/_/g, " ") || "-"}
      sx={{
        fontSize: "11px",
        fontWeight: 600,
        color: s.color,
        backgroundColor: s.bg,
        textTransform: "capitalize",
      }}
    />
  );
};

const SummaryTile = ({ label, value, accent }) => (
  <Box
    sx={{
      borderRadius: "12px",
      border: `1px solid ${palette.line}`,
      backgroundColor: "#fff",
      padding: "14px 16px",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        fontSize: "11.5px",
        fontWeight: 700,
        color: palette.slate,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      }}
    >
      {label}
    </Box>

    <Box
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: accent || palette.ink,
        marginTop: "4px",
      }}
    >
      {value}
    </Box>
  </Box>
);

const SessionCard = ({ s, showReason }) => (
  <Box
    sx={{
      border: `1px solid ${palette.line}`,
      borderRadius: "10px",
      padding: "12px 14px",
      backgroundColor: "#fff",
    }}
  >
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: "8px",
      }}
    >
      <Box>
        <Box
          sx={{
            fontSize: "13.5px",
            fontWeight: 700,
            color: palette.ink,
          }}
        >
          {formatDate(s.session_date)}
        </Box>

        <Box
          sx={{
            fontSize: "11.5px",
            color: palette.slate,
            marginTop: "1px",
          }}
        >
          {formatTime(s.start_time)} - {formatTime(s.end_time)} · #
          {s.session_number}
        </Box>
      </Box>

      <Box
        sx={{
          fontSize: "14.5px",
          fontWeight: 700,
          color: palette.ink,
          whiteSpace: "nowrap",
        }}
      >
        {currency(s.amount)}
      </Box>
    </Box>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "6px",
        marginTop: "10px",
      }}
    >
      <Box
        sx={{
          fontSize: "12.5px",
          fontWeight: 600,
          color: palette.ink,
        }}
      >
        {s.service_name}
      </Box>

      {s.session_type === "MAKEUP" && (
        <Chip
          size="small"
          label="Makeup"
          sx={{
            fontSize: "10.5px",
            fontWeight: 600,
            color: palette.amber,
            backgroundColor: palette.amberSoft,
            height: "20px",
          }}
        />
      )}

      <StatusChip
        value={s.status}
        styles={STATUS_STYLES}
      />

      <StatusChip
        value={s.billing_status}
        styles={BILLING_STATUS_STYLES}
      />
    </Box>

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        fontSize: "11.5px",
        color: palette.slate,
        marginTop: "8px",
      }}
    >
      <span>
        Unit price: {currency(s.unit_price)}
      </span>
    </Box>

    {showReason && s.reason && (
      <Box
        sx={{
          fontSize: "12px",
          color: palette.slate,
          marginTop: "6px",
          fontStyle: "italic",
        }}
      >
        {s.reason}
      </Box>
    )}
  </Box>
);

// ---------------------------------------------------------
// Summary widgets
// ---------------------------------------------------------
const SUMMARY_WIDGETS = [
  {
    key: "total",
    label: "Total Sessions",
    getValue: (s) => s.total_allocated_sessions ?? "-",
  },
  {
    key: "attended",
    label: "Attended",
    getValue: (s) => s.attended_sessions ?? "-",
    accent: "#0B6B5F",
  },
  {
    key: "absent",
    label: "Absent",
    getValue: (s) => s.absent_sessions ?? "-",
    accent: palette.coral,
  },
  {
    key: "makeup",
    label: "Makeup",
    getValue: (s) => s.makeup_sessions ?? "-",
    accent: palette.amber,
  },
  {
    key: "scheduled",
    label: "Scheduled",
    getValue: (s) => s.scheduled_sessions ?? "-",
  },
  {
    key: "cancelled",
    label: "Cancelled",
    getValue: (s) => s.cancelled_sessions ?? "-",
  },
  {
    key: "billable",
    label: "Billable Sessions",
    getValue: (s) => s.billable_sessions ?? "-",
  },
  {
    key: "amount",
    label: "Session Amount",
    getValue: (s) => currency(s.session_amount),
  },
];

// ---------------------------------------------------------
// Main component
// ---------------------------------------------------------
export default function InvoicePreviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const patientId = searchParams.get("patient_id");
  const invoiceId = searchParams.get("invoice_id");
  const branchId = searchParams.get("branch_id");

  const now = new Date();

  const year =
    Number(searchParams.get("year")) ||
    now.getFullYear();

  const month =
    Number(searchParams.get("month")) ||
    now.getMonth() + 1;

  const [invoice, setInvoice] = useState(null);
  const [branch, setBranch] = useState(null);

  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const [error, setError] = useState("");

  // -------------------------------------------------------
  // Organisation logo
  // -------------------------------------------------------
  // Prefer whatever is already in Redux (fast path — normal
  // in-app navigation from Home.js already populated this).
  // If this page is opened standalone (new tab / popup, e.g.
  // via window.open from a "Print" action) Redux may not be
  // populated yet, so we fall back to fetching current_user
  // directly.
  const reduxUserDetail = useSelector(
    (state) => state.userDetails?.detail
  );

  const [fallbackUserDetail, setFallbackUserDetail] =
    useState(null);

  const userDetail = reduxUserDetail || fallbackUserDetail;

  useEffect(() => {
    if (reduxUserDetail) return;

    const fetchCurrentUser = async () => {
      try {
        const response = await api.get(
          "app/user/current_user"
        );
        setFallbackUserDetail(response?.data || null);
      } catch (err) {
        console.error(
          "Failed to load current user for logo:",
          err
        );
      }
    };

    fetchCurrentUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orgLogoUrl = `${API_BASE_URL}${
    userDetail?.organisation_logo || DEFAULT_LOGO_PATH
  }`;

  // -------------------------------------------------------
  // Admin / Patient view
  // -------------------------------------------------------
  // When invoice_id is present, the invoice has already been
  // generated — this is a locked/historical preview. In that
  // case the view is always "patient" (no admin controls,
  // no toggle, no back button — only Print is functional).
  const isLockedInvoiceView = Boolean(invoiceId);

  const view = isLockedInvoiceView
    ? "patient"
    : searchParams.get("view") === "patient"
    ? "patient"
    : "admin";

  const [approvedShowWidgets, setApprovedShowWidgets] =
    useState(true);

  const [draftShowWidgets, setDraftShowWidgets] =
    useState(true);

  const hasPendingWidgetChange =
    draftShowWidgets !== approvedShowWidgets;

  // -------------------------------------------------------
  // Fetch invoice preview
  // -------------------------------------------------------
  const fetchInvoicePreview = async () => {
    if (!patientId && !invoiceId) {
      setError("Missing patient_id or invoice_id in the URL.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const url = invoiceId
        ? `app/invoice/${invoiceId}/preview/`
        : `app/invoice/preview?patient_id=${patientId}&year=${year}&month=${month}`;

      const response = await api.get(url);

      setInvoice(response?.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Failed to load invoice preview."
      );
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------
  // Load preview whenever patient/month/year/invoice changes
  // -------------------------------------------------------
  useEffect(() => {
    fetchInvoicePreview();
  }, [patientId, invoiceId, year, month]);

  // -------------------------------------------------------
  // Generate invoice
  // -------------------------------------------------------
  const handleGenerateInvoice = async () => {
    if (!patientId || generating) return;

    try {
      setGenerating(true);
      setError("");

      await api.post(
        "app/invoice/generate",
        {
          patient_id: Number(patientId),
          year,
          month,
        }
      );

      await fetchInvoicePreview();
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "Failed to generate invoice."
      );
    } finally {
      setGenerating(false);
    }
  };

  // -------------------------------------------------------
  // Branch details
  // -------------------------------------------------------
  useEffect(() => {
    if (!branchId) return;

    const fetchBranchDetails = async () => {
      try {
        const response = await api.get(
          `app/management/branches/?branch_id=${branchId}`
        );

        setBranch(response?.data?.[0] || null);
      } catch (err) {
        console.error(
          "Failed to load branch/org details:",
          err
        );
      }
    };

    fetchBranchDetails();
  }, [branchId]);

  // -------------------------------------------------------
  // View change
  // -------------------------------------------------------
  const handleViewChange = (nextView) => {
    const next = new URLSearchParams(searchParams);

    next.set("view", nextView);

    setSearchParams(next);
  };

  // -------------------------------------------------------
  // Widget settings
  // -------------------------------------------------------
  useEffect(() => {
    if (!branchId) return;

    try {
      const stored = localStorage.getItem(
        `invoicePreview_patientWidgetsApproved_${branchId}`
      );

      const approved =
        stored === null
          ? true
          : stored === "true";

      setApprovedShowWidgets(approved);
      setDraftShowWidgets(approved);
    } catch {
      setApprovedShowWidgets(true);
      setDraftShowWidgets(true);
    }
  }, [branchId]);

  const handleApproveWidgetsSetting = () => {
    setApprovedShowWidgets(draftShowWidgets);

    if (branchId) {
      try {
        localStorage.setItem(
          `invoicePreview_patientWidgetsApproved_${branchId}`,
          String(draftShowWidgets)
        );
      } catch {
        // Ignore localStorage failure
      }
    }
  };

  const handleDiscardWidgetsDraft = () => {
    setDraftShowWidgets(approvedShowWidgets);
  };

  // -------------------------------------------------------
  // Keep year/month in URL
  // -------------------------------------------------------
  useEffect(() => {
    if (
      invoiceId ||
      searchParams.get("year") ||
      searchParams.get("month")
    ) {
      return;
    }

    const next = new URLSearchParams(searchParams);

    next.set("year", String(year));
    next.set("month", String(month));

    setSearchParams(next, {
      replace: true,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------------------------------------------------------
  // Period change
  // -------------------------------------------------------
  const handlePeriodChange = (key, value) => {
    const next = new URLSearchParams(searchParams);

    next.delete("invoice_id");
    next.set(key, String(value));

    setSearchParams(next);
  };

  // -------------------------------------------------------
  // Back
  // -------------------------------------------------------
  const handleBack = () => {
    if (
      window.opener &&
      !window.opener.closed
    ) {
      window.close();
      return;
    }

    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/patients");
    }
  };

  // -------------------------------------------------------
  // Data
  // -------------------------------------------------------
  const {
    summary = {},
    sessions = [],
    adjustments = [],
  } = invoice || {};

  const periodLabel = invoice
    ? `${MONTH_NAMES[
        (invoice?.billing_period_month || month) - 1
      ]} ${
        invoice?.billing_period_year || year
      }`
    : `${MONTH_NAMES[month - 1]} ${year}`;

  // -------------------------------------------------------
  // Select styles
  // -------------------------------------------------------
  const periodSelectSx = {
    fontSize: "13px",
    fontWeight: 600,
    color: palette.ink,
    backgroundColor: palette.bg,
    borderRadius: "8px",

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: palette.line,
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: palette.slate,
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: palette.indigo,
    },
  };

  // -------------------------------------------------------
  // Render
  // -------------------------------------------------------
  return (
    <Box
      sx={{
        backgroundColor: palette.bg,
        minHeight: "100vh",
        paddingBottom: "48px",
      }}
    >
      {/* Top bar */}
      <Box
        className="no-print"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          padding: "14px 24px",
          borderBottom: `1px solid ${palette.line}`,
          backgroundColor: "#fff",
          position: "sticky",
          top: 0,
          zIndex: 10,
        }}
      >
        {!isLockedInvoiceView && (
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={handleBack}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: palette.slate,
            }}
          >
            Back to Patients
          </Button>
        )}

        {/* Admin / Patient — hidden once invoice is generated (locked view is always "patient") */}
        {!isLockedInvoiceView && (
          <ButtonGroup
            size="small"
            sx={{ borderRadius: "8px" }}
          >
            <Button
              variant={
                view === "admin"
                  ? "contained"
                  : "outlined"
              }
              onClick={() =>
                handleViewChange("admin")
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12.5px",
                backgroundColor:
                  view === "admin"
                    ? palette.indigo
                    : "transparent",
                borderColor: palette.indigo,
                color:
                  view === "admin"
                    ? "#fff"
                    : palette.indigo,
                "&:hover": {
                  backgroundColor:
                    view === "admin"
                      ? palette.indigoDark
                      : palette.bg,
                  borderColor: palette.indigo,
                },
              }}
            >
              Admin
            </Button>

            <Button
              variant={
                view === "patient"
                  ? "contained"
                  : "outlined"
              }
              onClick={() =>
                handleViewChange("patient")
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: "12.5px",
                backgroundColor:
                  view === "patient"
                    ? palette.indigo
                    : "transparent",
                borderColor: palette.indigo,
                color:
                  view === "patient"
                    ? "#fff"
                    : palette.indigo,
                "&:hover": {
                  backgroundColor:
                    view === "patient"
                      ? palette.indigoDark
                      : palette.bg,
                  borderColor: palette.indigo,
                },
              }}
            >
              Patient
            </Button>
          </ButtonGroup>
        )}

        {/* Month / Year — hidden once invoice is generated; period is fixed at that point */}
        {!isLockedInvoiceView && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FormControl size="small">
              <Select
                value={month}
                onChange={(e) =>
                  handlePeriodChange(
                    "month",
                    e.target.value
                  )
                }
                sx={periodSelectSx}
              >
                {MONTH_NAMES.map(
                  (name, idx) => (
                    <MenuItem
                      key={name}
                      value={idx + 1}
                    >
                      {name}
                    </MenuItem>
                  )
                )}
              </Select>
            </FormControl>

            <FormControl size="small">
              <Select
                value={year}
                onChange={(e) =>
                  handlePeriodChange(
                    "year",
                    e.target.value
                  )
                }
                sx={periodSelectSx}
              >
                {YEAR_OPTIONS.map((y) => (
                  <MenuItem
                    key={y}
                    value={y}
                  >
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Generate / Print */}
        {isLockedInvoiceView ? (
          // invoice_id present → invoice is already generated, only Print is relevant
          <Button
            variant="contained"
            startIcon={
              <PrintRoundedIcon
                sx={{ fontSize: "18px" }}
              />
            }
            onClick={() =>
              window.print()
            }
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              backgroundColor:
                palette.indigo,
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  palette.indigoDark,
                boxShadow: "none",
              },
            }}
          >
            Print
          </Button>
        ) : !invoice?.invoice_generated ? (
          <Button
            variant="contained"
            onClick={handleGenerateInvoice}
            disabled={
              loading ||
              generating ||
              !!error
            }
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              backgroundColor: palette.indigo,
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  palette.indigoDark,
                boxShadow: "none",
              },
            }}
          >
            {generating ? (
              <>
                <CircularProgress
                  size={16}
                  sx={{
                    color: "#fff",
                    marginRight: "8px",
                  }}
                />
                Generating...
              </>
            ) : (
              "Generate Invoice"
            )}
          </Button>
        ) : (
          <Button
            variant="contained"
            startIcon={
              <PrintRoundedIcon
                sx={{ fontSize: "18px" }}
              />
            }
            onClick={() =>
              window.print()
            }
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              backgroundColor:
                palette.indigo,
              boxShadow: "none",
              "&:hover": {
                backgroundColor:
                  palette.indigoDark,
                boxShadow: "none",
              },
            }}
          >
            Print
          </Button>
        )}
      </Box>

      {/* Generated invoice indicator */}
      {!loading &&
        !error &&
        invoice?.invoice_generated && (
          <Container
            maxWidth="md"
            sx={{
              paddingTop: "12px",
            }}
          >
            <Chip
              label={`Invoice: ${invoice.invoice_no}`}
              sx={{
                fontSize: "11.5px",
                fontWeight: 600,
                color: palette.teal,
                backgroundColor:
                  palette.tealSoft,
              }}
            />
          </Container>
        )}

      {/* Loading */}
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress
            sx={{
              color: palette.indigo,
            }}
          />
        </Box>
      )}

      {/* Error */}
      {!loading && error && (
        <Container
          sx={{
            paddingTop: "40px",
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: palette.coral,
              marginBottom: "16px",
            }}
          >
            {error}
          </Box>
        </Container>
      )}

      {/* Main content */}
      {!loading && !error && (
        <Container
          maxWidth="md"
          sx={{
            paddingTop: {
              xs: "16px",
              sm: "28px",
            },
            paddingLeft: {
              xs: "8px",
              sm: "24px",
            },
            paddingRight: {
              xs: "8px",
              sm: "24px",
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              borderRadius: "14px",
              border: `1px solid ${palette.line}`,
              backgroundColor: "#fff",
              overflow: "hidden",
            }}
          >
            {/* Letterhead */}
            {branch && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  flexWrap: "wrap",
                  padding: {
                    xs: "18px 16px",
                    sm: "22px 28px",
                  },
                  background: `linear-gradient(135deg, ${palette.indigo}, ${palette.indigoDark})`,
                }}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "10px",
                    backgroundColor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    overflow: "hidden",
                    boxShadow:
                      "0 2px 6px rgba(0,0,0,0.15)",
                  }}
                >
                  <Box
                    component="img"
                    src={orgLogoUrl}
                    alt={
                      branch.name ||
                      "Organization logo"
                    }
                    sx={{
                      height: "80%",
                      width: "80%",
                      objectFit: "contain",
                    }}
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = `${API_BASE_URL}${DEFAULT_LOGO_PATH}`;
                    }}
                  />
                </Box>

                <Box>
                  <Box
                    sx={{
                      fontSize: "18px",
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    {branch.name ||
                      "Clinic"}
                  </Box>

                  <Box
                    sx={{
                      fontSize: "12px",
                      color:
                        "rgba(255,255,255,0.8)",
                      marginTop: "3px",
                    }}
                  >
                    {[
                      branch.address,
                      branch.city,
                      branch.country,
                      branch.postal_code,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Box>

                  <Box
                    sx={{
                      fontSize: "12px",
                      color:
                        "rgba(255,255,255,0.8)",
                      marginTop: "1px",
                    }}
                  >
                    {branch.contact_number}
                  </Box>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                padding: {
                  xs: "16px",
                  sm: "28px",
                },
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "space-between",
                  alignItems:
                    "flex-start",
                  flexWrap: "wrap",
                  gap: "12px",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: palette.ink,
                    }}
                  >
                    {invoice?.patient_name ||
                      "Patient"}
                  </Box>

                  <Box
                    sx={{
                      fontSize: "13px",
                      color: palette.slate,
                      marginTop: "2px",
                    }}
                  >
                    Patient ID:{" "}
                    {invoice?.patient_id ??
                      patientId}
                  </Box>

                  {invoice?.invoice_generated && (
                    <Box
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: palette.teal,
                        marginTop: "5px",
                      }}
                    >
                      Invoice:{" "}
                      {invoice.invoice_no}
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    textAlign: "right",
                  }}
                >
                  <Box
                    sx={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: palette.slate,
                      textTransform:
                        "uppercase",
                      letterSpacing:
                        "0.03em",
                      marginBottom: "6px",
                    }}
                  >
                    Billing Period
                  </Box>

                  <Box
                    sx={{
                      display:
                        "inline-block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: palette.indigo,
                      backgroundColor:
                        palette.indigoSoft,
                      borderRadius: "8px",
                      padding:
                        "4px 12px",
                    }}
                  >
                    {periodLabel}
                  </Box>
                </Box>
              </Box>

              <Divider
                sx={{
                  margin: "20px 0",
                  borderColor: palette.line,
                }}
              />

              {/* Admin widget controls */}
              {view === "admin" && (
                <Box
                  className="no-print"
                  sx={{
                    marginBottom: "12px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems:
                        "center",
                      flexWrap: "wrap",
                      gap: "10px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems:
                          "center",
                        gap: "6px",
                      }}
                    >
                      <Switch
                        checked={
                          draftShowWidgets
                        }
                        onChange={(e) =>
                          setDraftShowWidgets(
                            e.target
                              .checked
                          )
                        }
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked":
                            {
                              color:
                                palette.indigo,
                            },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor:
                                palette.indigo,
                            },
                        }}
                      />

                      <Box
                        sx={{
                          fontSize:
                            "13px",
                          fontWeight: 600,
                          color:
                            palette.ink,
                        }}
                      >
                        Show summary widgets
                        to patients
                      </Box>
                    </Box>

                    {hasPendingWidgetChange && (
                      <Chip
                        size="small"
                        label="Unsaved change"
                        sx={{
                          fontSize:
                            "11px",
                          fontWeight: 600,
                          color:
                            palette.amber,
                          backgroundColor:
                            palette.amberSoft,
                        }}
                      />
                    )}

                    <Box
                      sx={{
                        flexGrow: 1,
                      }}
                    />

                    <Button
                      size="small"
                      onClick={() =>
                        handleViewChange(
                          "patient"
                        )
                      }
                      sx={{
                        textTransform:
                          "none",
                        fontWeight: 600,
                        fontSize:
                          "12.5px",
                        color:
                          palette.slate,
                      }}
                    >
                      Preview as Patient
                    </Button>

                    {hasPendingWidgetChange && (
                      <>
                        <Button
                          size="small"
                          startIcon={
                            <ReplayRoundedIcon
                              sx={{
                                fontSize:
                                  "15px",
                              }}
                            />
                          }
                          onClick={
                            handleDiscardWidgetsDraft
                          }
                          sx={{
                            textTransform:
                              "none",
                            fontWeight: 600,
                            fontSize:
                              "12.5px",
                            color:
                              palette.slate,
                          }}
                        >
                          Discard
                        </Button>

                        <Button
                          size="small"
                          variant="contained"
                          startIcon={
                            <CheckRoundedIcon
                              sx={{
                                fontSize:
                                  "16px",
                              }}
                            />
                          }
                          onClick={
                            handleApproveWidgetsSetting
                          }
                          sx={{
                            textTransform:
                              "none",
                            fontWeight: 600,
                            fontSize:
                              "12.5px",
                            borderRadius:
                              "8px",
                            backgroundColor:
                              palette.indigo,
                            boxShadow:
                              "none",
                            "&:hover": {
                              backgroundColor:
                                palette.indigoDark,
                              boxShadow:
                                "none",
                            },
                          }}
                        >
                          Approve
                        </Button>
                      </>
                    )}
                  </Box>
                </Box>
              )}

              {/* Summary */}
              {(view === "admin" ||
                draftShowWidgets) && (
                <Grid
                  container
                  spacing={1.5}
                >
                  {SUMMARY_WIDGETS.map(
                    (w) => (
                      <Grid
                        key={w.key}
                        size={{
                          xs: 6,
                          sm: 3,
                        }}
                      >
                        <SummaryTile
                          label={w.label}
                          value={w.getValue(
                            summary
                          )}
                          accent={
                            w.accent
                          }
                        />
                      </Grid>
                    )
                  )}
                </Grid>
              )}

              {/* Sessions */}
              <Box
                sx={{
                  marginTop: "28px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "14px",
                    fontWeight: 700,
                    color: palette.ink,
                    marginBottom:
                      "10px",
                  }}
                >
                  Session Details
                </Box>

                {/* Mobile */}
                <Box
                  className="session-cards"
                  sx={{
                    display: {
                      xs: "flex",
                      md: "none",
                    },
                    flexDirection:
                      "column",
                    gap: "10px",
                  }}
                >
                  {sessions.map(
                    (s) => (
                      <SessionCard
                        key={
                          s.session_id
                        }
                        s={s}
                        showReason={
                          view ===
                          "admin"
                        }
                      />
                    )
                  )}

                  {sessions.length ===
                    0 && (
                    <Box
                      sx={{
                        fontSize:
                          "13px",
                        color:
                          palette.slate,
                        padding:
                          "20px",
                        textAlign:
                          "center",
                        border: `1px solid ${palette.line}`,
                        borderRadius:
                          "10px",
                      }}
                    >
                      No sessions found
                      for this billing
                      period.
                    </Box>
                  )}
                </Box>

                {/* Desktop */}
                <TableContainer
                  className="session-table"
                  sx={{
                    display: {
                      xs: "none",
                      md: "block",
                    },
                    border: `1px solid ${palette.line}`,
                    borderRadius:
                      "10px",
                    overflowX:
                      "auto",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          backgroundColor:
                            palette.bg,
                        }}
                      >
                        {(view ===
                        "admin"
                          ? [
                              "#",
                              "Date",
                              "Service",
                              "Status",
                              "Billing",
                              "Unit Price",
                              "Amount",
                              "Reason",
                            ]
                          : [
                              "#",
                              "Date",
                              "Service",
                              "Status",
                              "Billing",
                              "Unit Price",
                              "Amount",
                            ]
                        ).map((h) => (
                          <TableCell
                            key={h}
                            align={
                              h === "#"
                                ? "center"
                                : "left"
                            }
                            sx={{
                              fontSize:
                                "11px",
                              fontWeight: 700,
                              color:
                                palette.slate,
                              textTransform:
                                "uppercase",
                              letterSpacing:
                                "0.03em",
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {sessions.map(
                        (s) => (
                          <TableRow
                            key={
                              s.session_id
                            }
                            sx={{
                              "&:last-child td":
                                {
                                  border: 0,
                                },
                            }}
                          >
                            <TableCell
                              align="center"
                              sx={{
                                fontSize:
                                  "12.5px",
                              }}
                            >
                              {
                                s.session_number
                              }
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize:
                                  "12.5px",
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {formatDate(
                                s.session_date
                              )}

                              <Box
                                sx={{
                                  fontSize:
                                    "11px",
                                  color:
                                    palette.slate,
                                }}
                              >
                                {formatTime(
                                  s.start_time
                                )}{" "}
                                -{" "}
                                {formatTime(
                                  s.end_time
                                )}
                              </Box>
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize:
                                  "12.5px",
                              }}
                            >
                              {
                                s.service_name
                              }

                              {s.session_type ===
                                "MAKEUP" && (
                                <Chip
                                  size="small"
                                  label="Makeup"
                                  sx={{
                                    fontSize:
                                      "10px",
                                    fontWeight:
                                      600,
                                    color:
                                      palette.amber,
                                    backgroundColor:
                                      palette.amberSoft,
                                    height:
                                      "18px",
                                    marginLeft:
                                      "6px",
                                  }}
                                />
                              )}
                            </TableCell>

                            <TableCell>
                              <StatusChip
                                value={
                                  s.status
                                }
                                styles={
                                  STATUS_STYLES
                                }
                              />
                            </TableCell>

                            <TableCell>
                              <StatusChip
                                value={
                                  s.billing_status
                                }
                                styles={
                                  BILLING_STATUS_STYLES
                                }
                              />
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize:
                                  "12.5px",
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {currency(
                                s.unit_price
                              )}
                            </TableCell>

                            <TableCell
                              sx={{
                                fontSize:
                                  "12.5px",
                                fontWeight: 600,
                                whiteSpace:
                                  "nowrap",
                              }}
                            >
                              {currency(
                                s.amount
                              )}
                            </TableCell>

                            {view ===
                              "admin" && (
                              <TableCell
                                sx={{
                                  fontSize:
                                    "12.5px",
                                  color:
                                    palette.slate,
                                  maxWidth: 160,
                                }}
                              >
                                {s.reason ||
                                  "-"}
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      )}

                      {sessions.length ===
                        0 && (
                        <TableRow>
                          <TableCell
                            colSpan={
                              view ===
                              "admin"
                                ? 8
                                : 7
                            }
                            align="center"
                            sx={{
                              fontSize:
                                "13px",
                              color:
                                palette.slate,
                              padding:
                                "20px",
                            }}
                          >
                            No sessions found
                            for this billing
                            period.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Adjustments */}
              {adjustments.length >
                0 && (
                <Box
                  sx={{
                    marginTop:
                      "24px",
                  }}
                >
                  <Box
                    sx={{
                      fontSize:
                        "14px",
                      fontWeight: 700,
                      color:
                        palette.ink,
                      marginBottom:
                        "10px",
                    }}
                  >
                    Adjustments
                  </Box>

                  <TableContainer
                    sx={{
                      border: `1px solid ${palette.line}`,
                      borderRadius:
                        "10px",
                      overflow:
                        "hidden",
                    }}
                  >
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            backgroundColor:
                              palette.bg,
                          }}
                        >
                          <TableCell
                            sx={{
                              fontSize:
                                "11px",
                              fontWeight:
                                700,
                              color:
                                palette.slate,
                              textTransform:
                                "uppercase",
                            }}
                          >
                            Description
                          </TableCell>

                          <TableCell
                            sx={{
                              fontSize:
                                "11px",
                              fontWeight:
                                700,
                              color:
                                palette.slate,
                              textTransform:
                                "uppercase",
                            }}
                          >
                            Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {adjustments.map(
                          (a, idx) => (
                            <TableRow
                              key={idx}
                            >
                              <TableCell
                                sx={{
                                  fontSize:
                                    "12.5px",
                                }}
                              >
                                {getAdjustmentDescription(
                                  a
                                )}
                              </TableCell>

                              <TableCell
                                sx={{
                                  fontSize:
                                    "12.5px",
                                }}
                              >
                                {currency(
                                  a.amount
                                )}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* Totals */}
              <Divider
                sx={{
                  margin: "24px 0",
                  borderColor:
                    palette.line,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  justifyContent:
                    "flex-end",
                }}
              >
                <Box
                  sx={{
                    minWidth: 260,
                  }}
                >
                  <Box
                    sx={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      fontSize:
                        "13px",
                      color:
                        palette.slate,
                      marginBottom:
                        "6px",
                    }}
                  >
                    <span>
                      Session Amount
                    </span>

                    <span>
                      {currency(
                        summary.session_amount
                      )}
                    </span>
                  </Box>

                  <Box
                    sx={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      fontSize:
                        "13px",
                      color:
                        palette.slate,
                      marginBottom:
                        "10px",
                    }}
                  >
                    <span>
                      Adjustment Credit
                    </span>

                    <span>
                      -
                      {currency(
                        summary.adjustment_credit
                      )}
                    </span>
                  </Box>

                  <Divider
                    sx={{
                      borderColor:
                        palette.line,
                      marginBottom:
                        "10px",
                    }}
                  />

                  <Box
                    sx={{
                      display:
                        "flex",
                      justifyContent:
                        "space-between",
                      alignItems:
                        "center",
                      fontSize:
                        "17px",
                      fontWeight: 700,
                      color:
                        palette.indigo,
                      backgroundColor:
                        palette.indigoSoft,
                      borderRadius:
                        "10px",
                      padding:
                        "10px 14px",
                    }}
                  >
                    <span>
                      Final Amount
                    </span>

                    <span>
                      {currency(
                        summary.final_amount
                      )}
                    </span>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Container>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          .no-print {
            display: none !important;
          }

          .session-cards {
            display: none !important;
          }

          .session-table {
            display: block !important;
          }

          body {
            background: #fff !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }

          @page {
            margin: 0;
          }

          body {
            padding: 12mm 10mm !important;
          }
        }
      `}</style>
    </Box>
  );
}